import {
  Motion,
  Pattern,
  type DefIndex,
  type EmitterDef,
  type EnemyDef,
  type LevelBlock,
  type LevelDef,
  type ObstacleDef,
  type ObstaclePlacement,
  type PhaseDef,
  type WaveDef,
} from '../sim/defs';
import { Fam } from '../sim/pools/BulletPool';

/* ═══════════════════════════════════════════════════════════════════════════
   Les helpers d'écriture du contenu.

   Le contenu est en TYPESCRIPT DÉCLARATIF, pas en JSON (PLAN.md §3.3) :
   autocomplétion des identifiants, validation à la compilation, référencement
   croisé sûr, rechargement à chaud gratuit. Le JSON est réservé à ce qui doit
   changer sans recompiler — c'est-à-dire au seul `balance.json`.

   Ces fonctions ne font presque rien : elles existent pour que le typage soit
   inféré au bon endroit et pour poser des valeurs par défaut sensées, afin
   qu'un fichier d'ennemi tienne en quinze lignes lisibles.
   ═══════════════════════════════════════════════════════════════════════════ */

export { Motion, Pattern };

/** Ne tire pas. Le popcorn n'a pas d'émetteur, et c'est structurant : il
 *  remplit les creux du rythme sans coûter d'attention au joueur. */
export const NO_EMITTER = {
  pattern: Pattern.None,
  every: 999,
  count: 0,
  spreadDeg: 0,
  speed: 0,
  stepDeg: 0,
  firstDelay: 999,
  family: Fam.EnemyRed,
} as const;

/** Une phase EN ÉCRITURE : son émetteur peut être partiel (fusionné avec
 *  `NO_EMITTER`), exactement comme l'émetteur de base d'un ennemi. */
type PhaseInput = Omit<PhaseDef, 'emitter'> & { emitter: Partial<EmitterDef> };

type EnemyInput = Omit<EnemyDef, 'motion' | 'emitter' | 'phases'> & {
  motion: Partial<EnemyDef['motion']> & { kind: number };
  emitter?: Partial<EnemyDef['emitter']>;
  phases?: readonly PhaseInput[];
};

export function defineEnemy(d: EnemyInput): EnemyDef {
  const { phases, ...rest } = d;
  const out: EnemyDef = {
    ...rest,
    motion: {
      kind: d.motion.kind,
      speed: d.motion.speed ?? 300,
      amplitude: d.motion.amplitude ?? 0,
      frequency: d.motion.frequency ?? 0,
      holdY: d.motion.holdY ?? 300,
      // ⚠️ À défaut d'être recopié ici, un `hold` écrit dans un fichier
      // d'ennemi serait silencieusement PERDU : cette fonction reconstruit
      // l'objet champ par champ au lieu de l'étaler, pour poser ses valeurs par
      // défaut. Tout champ ajouté à `MotionDef` doit donc être ajouté ici
      // aussi — l'oubli ne produit ni erreur de compilation ni test rouge, juste
      // un Dard qui plonge sans attendre.
      hold: d.motion.hold ?? 0,
    },
    emitter: { ...NO_EMITTER, ...d.emitter },
  };

  if (!phases) return out;

  /* Validation STRUCTURELLE des phases au chargement (échec à l'écriture, jamais
     en plein combat). Chaque phase a au moins UN déclencheur ; les seuils de PV
     sont dans ]0,1[ et strictement DÉcroissants entre phases qui les emploient ;
     les seuils de parties sont des entiers ≥ 1, strictement CROissants, et bornés
     au nombre de parties déclarées (un seuil au-delà rendrait la phase
     inatteignable). `phaseSystem` s'appuie sur ces monotonies. */
  const problems: string[] = [];
  const totalParts = d.parts?.length ?? 0;
  let prevHp = 1;
  let prevParts = 0;
  phases.forEach((ph, i) => {
    if (ph.atHpFrac === undefined && ph.atPartsLost === undefined) {
      problems.push(`${d.id} : phase #${i} — au moins un déclencheur (atHpFrac ou atPartsLost) requis`);
    }
    if (ph.atHpFrac !== undefined) {
      if (!(ph.atHpFrac > 0 && ph.atHpFrac < 1)) {
        problems.push(`${d.id} : phase #${i} — atHpFrac ${ph.atHpFrac} hors de ]0,1[`);
      }
      if (ph.atHpFrac >= prevHp) {
        problems.push(`${d.id} : phase #${i} — atHpFrac ${ph.atHpFrac} pas strictement sous ${prevHp}`);
      }
      prevHp = ph.atHpFrac;
    }
    if (ph.atPartsLost !== undefined) {
      if (!(Number.isInteger(ph.atPartsLost) && ph.atPartsLost >= 1)) {
        problems.push(`${d.id} : phase #${i} — atPartsLost ${ph.atPartsLost} doit être un entier ≥ 1`);
      }
      if (ph.atPartsLost <= prevParts) {
        problems.push(
          `${d.id} : phase #${i} — atPartsLost ${ph.atPartsLost} pas strictement au-dessus de ${prevParts}`,
        );
      }
      if (ph.atPartsLost > totalParts) {
        problems.push(
          `${d.id} : phase #${i} — atPartsLost ${ph.atPartsLost} > ${totalParts} parties déclarées (phase inatteignable)`,
        );
      }
      prevParts = ph.atPartsLost;
    }
  });
  if (problems.length > 0) {
    throw new Error(`Phases de boss invalides :\n  - ${problems.join('\n  - ')}`);
  }

  return {
    ...out,
    // Chaque émetteur de phase reçoit les mêmes défauts que l'émetteur de base.
    phases: phases.map((ph) => ({ ...ph, emitter: { ...NO_EMITTER, ...ph.emitter } })),
  };
}

export function defineWave(d: WaveDef): WaveDef {
  return d;
}

/** `true` si `v` est un nombre fini — le rejet des `NaN`/`Infinity` est ici
 *  aussi important que celui des valeurs hors bornes : un `at` à `NaN` poserait
 *  un obstacle à une ordonnée `NaN`, invisible ET jamais culé, qui saturerait le
 *  pool en silence. */
function fini(v: unknown): v is number {
  return typeof v === 'number' && Number.isFinite(v);
}

/**
 * Vérifie la STRUCTURE d'un placement d'obstacle, sans rien savoir du catalogue.
 *
 * On échoue au CHARGEMENT (défineLevel jette) plutôt qu'en jeu : un `at` négatif
 * ou une abscisse `NaN` ne se voit pas à la lecture d'un fichier de niveau, mais
 * produit un décor placé n'importe où — ou jamais. La vérification de
 * l'EXISTENCE des identifiants (obstacle, ennemi de sentinelle) est faite
 * ailleurs, par `checkLevelObstacles`, parce qu'elle exige le catalogue résolu,
 * que ce helper n'a pas.
 */
function problemesPlacement(levelId: string, i: number, pl: ObstaclePlacement): string[] {
  const p: string[] = [];
  const où = `niveau ${levelId} : obstacle #${i}`;
  // `at` NÉGATIF ferait naître l'obstacle SOUS le haut du cadre, donc déjà entré
  // — la section perdrait son échelonnement dans le temps.
  if (!fini(pl.at) || pl.at < 0) p.push(`${où} : « at » doit être un nombre fini ≥ 0`);
  if (!fini(pl.x)) p.push(`${où} : « x » doit être un nombre fini`);
  if (typeof pl.obstacle !== 'string' || pl.obstacle.length === 0) {
    p.push(`${où} : « obstacle » doit être un identifiant non vide`);
  }
  if (pl.sentinels) {
    pl.sentinels.forEach((s, j) => {
      if (typeof s.enemy !== 'string' || s.enemy.length === 0) {
        p.push(`${où} : sentinelle #${j} — « enemy » doit être un identifiant non vide`);
      }
      if (!fini(s.offX) || !fini(s.offY)) {
        p.push(`${où} : sentinelle #${j} — décalages « offX »/« offY » non finis`);
      }
    });
  }
  return p;
}

export function defineLevel(d: LevelDef): LevelDef {
  // Validation de STRUCTURE au chargement du fichier — les références croisées
  // (obstacle/ennemi existants) sont laissées à `checkLevelObstacles`, qui a le
  // catalogue. Les blocs sans `obstacles` ne coûtent rien : la boucle ne les
  // visite pas.
  const problems: string[] = [];
  d.blocks.forEach((b: LevelBlock) => {
    b.obstacles?.forEach((pl, i) => problems.push(...problemesPlacement(d.id, i, pl)));
  });
  if (problems.length > 0) {
    throw new Error(`Placement d'obstacle invalide :\n  - ${problems.join('\n  - ')}`);
  }
  return d;
}

/**
 * Vérifie les RÉFÉRENCES CROISÉES des placements d'obstacles d'un catalogue :
 * chaque obstacle posé et chaque ennemi de sentinelle doit exister. Retourne la
 * liste des problèmes (vide si tout est correct), à la manière de
 * `validateShips`/`validateWeapons`.
 *
 * ⚠️ Séparée de la validation de structure (`defineLevel`) parce qu'elle exige
 * le catalogue RÉSOLU — `obstacleByI`, `enemyByI` — que la fonction d'écriture
 * d'un fichier n'a pas sous la main. Elle est destinée à être appelée par
 * `validate()` dans le registre, au même titre que la vérification des vagues :
 * un obstacle renommé y ferait sinon un placement silencieusement raté, un trou
 * dans le décor que personne ne remarque avant la sortie.
 */
export function checkLevelObstacles(index: DefIndex): string[] {
  const problems: string[] = [];
  for (const l of index.levels.values()) {
    l.blocks.forEach((b) => {
      b.obstacles?.forEach((pl, i) => {
        if (!index.obstacleByI.has(pl.obstacle)) {
          problems.push(`niveau ${l.id} : obstacle inconnu « ${pl.obstacle} » (#${i})`);
        }
        pl.sentinels?.forEach((s, j) => {
          if (!index.enemyByI.has(s.enemy)) {
            problems.push(
              `niveau ${l.id} : obstacle #${i}, sentinelle #${j} — ennemi inconnu « ${s.enemy} »`,
            );
          }
        });
      });
    });
  }
  return problems;
}

/** Un obstacle de décor collisionnable. `destructible` absent = vrai (un petit
 *  astéroïde qu'on fait sauter) ; le poser à `false` fait un rocher qu'on ne
 *  peut qu'esquiver. `score` absent = zéro : détruire un obstacle dégage du
 *  terrain, ce n'est pas d'abord une source de points. */
export function defineObstacle(d: ObstacleDef): ObstacleDef {
  return d;
}
